import { measureLayout, projectRows } from './geometry';
export const automaticClasses = ['upward', 'backfill', 'downward', 'lead'];
export function isActivated(state) {
    return !state.disposed && (state.source.status === 'active' || state.source.status === 'exhausted');
}
export function liveEligible(state, config) {
    return (isActivated(state) &&
        state.source.mode === 'live' &&
        state.initial === 'committed' &&
        state.measurement.viewportHeight > 0 &&
        state.measurement.scrollTop <= config.pinnedTolerance &&
        (state.caughtUp || state.liveCatchUp === 'active' || state.liveTopRecovery));
}
export function leadEligible(state, adapter) {
    return (isActivated(state) &&
        state.source.mode === 'live' &&
        state.source.selectionKind === 'single-symbol' &&
        state.roundSymbols.length > 0 &&
        adapter.declaresLeadSet);
}
export function deriveIntent(state, adapter, config) {
    if (!isActivated(state) ||
        state.fetch.status !== 'idle' ||
        state.roundSymbols.length === 0 ||
        state.measurement.viewportHeight <= 0) {
        return null;
    }
    const base = { symbols: state.roundSymbols, query: state.source.query };
    const initial = { ...base, kind: 'initial', expectedCount: config.pageSize };
    if (state.initial === 'uncommitted')
        return { instruction: initial, automaticClass: null };
    const newest = state.rows[0];
    const oldest = state.rows[state.rows.length - 1];
    if (state.manualUp && newest !== undefined) {
        return {
            instruction: {
                ...base,
                kind: 'up',
                position: adapter.cursorOf(newest),
                expectedCount: config.pageSize,
                cause: 'manual',
            },
            automaticClass: null,
        };
    }
    const layout = measureLayout(projectRows(state, adapter), state.measurement, config);
    const start = automaticClasses.indexOf(state.nextAutomaticClass);
    for (let i = 0; i < automaticClasses.length; i++) {
        const automaticClass = automaticClasses[(start + i) % automaticClasses.length];
        let instruction = null;
        switch (automaticClass) {
            case 'upward': {
                const live = liveEligible(state, config);
                const cause = state.liveCatchUp === 'active' && live
                    ? 'live'
                    : (layout.topVisible || state.liveTopRecovery) &&
                        !state.caughtUp &&
                        state.liveCatchUp === 'inactive'
                        ? 'buffer'
                        : live && state.liveDue
                            ? 'live'
                            : null;
                if (cause && newest !== undefined) {
                    instruction = {
                        ...base,
                        kind: 'up',
                        position: adapter.cursorOf(newest),
                        expectedCount: config.pageSize,
                        cause,
                    };
                }
                else if (live && state.liveDue && newest === undefined)
                    instruction = initial;
                break;
            }
            case 'downward':
                if (oldest !== undefined && layout.bottomVisible && !state.reachedEnd) {
                    instruction = {
                        ...base,
                        kind: 'down',
                        position: adapter.cursorOf(oldest),
                        expectedCount: config.pageSize,
                    };
                }
                break;
            case 'backfill':
                if (state.backfill.status === 'paging') {
                    const pass = state.backfill;
                    instruction = {
                        kind: 'backfill',
                        symbols: Object.freeze(pass.entries.map((entry) => entry.symbol)),
                        query: base.query,
                        newestCursor: pass.newestCursor,
                        oldestCursor: pass.oldestCursor,
                        page: pass.page,
                        limit: config.pageSize,
                    };
                }
                else if (newest !== undefined && state.pending.length > 0) {
                    instruction = {
                        kind: 'backfill',
                        symbols: Object.freeze(state.pending.map((entry) => entry.symbol)),
                        query: base.query,
                        newestCursor: adapter.cursorOf(newest),
                        oldestCursor: adapter.cursorOf(oldest),
                        page: { kind: 'first' },
                        limit: config.pageSize,
                    };
                }
                break;
            case 'lead':
                if (leadEligible(state, adapter) && state.lead.refresh.status !== 'current') {
                    const pass = state.lead.refresh;
                    instruction = {
                        kind: 'lead',
                        symbols: pass.status === 'paging' ? pass.symbols : base.symbols,
                        query: base.query,
                        page: pass.status === 'paging' ? pass.page : { kind: 'first' },
                        limit: config.pageSize,
                    };
                }
                break;
        }
        if (instruction)
            return { instruction, automaticClass };
    }
    return null;
}
export function roundReady(state, adapter, config) {
    if (!isActivated(state) || state.fetch.status === 'in-flight')
        return false;
    if (state.fetch.status === 'failed' || state.roundSymbols.length === 0)
        return true;
    if (state.initial === 'uncommitted')
        return false;
    if (state.backfill.status === 'paging' || (state.rows.length > 0 && state.pending.length > 0))
        return false;
    const layout = measureLayout(projectRows(state, adapter), state.measurement, config);
    return (!((layout.topVisible || state.liveTopRecovery) && !state.caughtUp && state.liveCatchUp === 'inactive') &&
        !(layout.bottomVisible && !state.reachedEnd));
}
export function presentation(state) {
    if (state.fetch.status === 'failed')
        return 'error';
    if (state.rows.length > 0 || state.lead.rows.length > 0)
        return 'rows';
    if (state.source.status === 'no-symbols' || state.initial === 'committed')
        return 'empty';
    return 'loading';
}
//# sourceMappingURL=selectors.js.map